// Linke Spalte der Lead-Seite, Reihenfolge und Gruppierung 1:1 wie Close („Details“):
//   1. Über (Adresse, Website, Beschreibung)   = Close „ABOUT“
//   2. Follow-ups (offene Aufgaben, „+“)         = Close „TASKS“
//   3. Opportunities                             = Close „OPPORTUNITIES“
//   4. Kontakte (Lupe, „+“)                      = Close „CONTACTS“
//   5. Felder (nur ausgefüllte, „+“, „…“)        = Close „CUSTOM FIELDS“
// Oben die Reiter „Details | Dateien“ wie Close (Dateien: DateienReiter.tsx).
// Abschnitte schlank ohne Karten (Optik vom 14.10.), Kopf in Großbuchstaben mit Zähler und Aktionen rechts wie Close.
import { Fragment, useEffect, useState, type ReactNode } from "react";
import { leadsApi, type Aufgabe, type Kontakt, type Lead, type LeadDetail } from "../../api";
import { Avatar } from "../Bausteine";
import { T } from "../../texte";
import { AufgabeZeile, aufgabenSortieren, useFollowUpAnlegen } from "../Aufgaben";
import { Icon } from "../Icon";
import { heute, tagPlus } from "../../werkzeuge/zeit";
import { useMeldung } from "../Meldungen";
import { Abschnitt } from "./Abschnitt";
import { AdresseBearbeiten } from "./AdresseBearbeiten";
import { OpportunitiesAbschnitt } from "./OpportunitiesAbschnitt";
import { TL } from "./leadTexte";
import { DateienReiter } from "./DateienReiter";
import { FelderAbschnitt } from "./CustomFields";
import { IntegrationLinks } from "./IntegrationLinks";
import { KontakteKarte } from "./Kontakte";
import "../../styles/lead.css";
import "../../styles/linke-spalte.css";

interface Props {
  detail: LeadDetail;
  hervorheben?: string | null;
  onLead: (l: Lead) => void;
  onKontakt: (k: Kontakt) => void;
  onKontakteOrdnen: (ids: string[]) => void;
  onAufgabe: (a: Aufgabe) => void;
}

type Reiter = "details" | "dateien";

export function LinkeSpalte({ detail, hervorheben, onLead, onKontakt, onKontakteOrdnen, onAufgabe }: Props) {
  const { lead, contacts, opportunities, tasks } = detail;
  const [reiter, setReiter] = useState<Reiter>("details");
  useEffect(() => setReiter("details"), [lead.id]);
  // „Über“ ist beim Öffnen eines Leads immer aufgeklappt (auch leer, auch wenn es vorher zugeklappt war); Zuklappen gilt nur bis zum nächsten Lead
  const [ueberZu, setUeberZu] = useState(false);
  const [ueberFuer, setUeberFuer] = useState(lead.id);
  if (ueberFuer !== lead.id) {
    setUeberFuer(lead.id);
    setUeberZu(false);
  }
  return (
    <>
      <div className="ls-tabs" role="tablist" aria-label="Lead-Daten">
        {(
          [
            ["details", "Details"],
            ["dateien", "Dateien"],
          ] as const
        ).map(([id, text]) => (
          <button key={id} type="button" role="tab" aria-selected={reiter === id} className={`ls-tab${reiter === id ? " ls-tab--an" : ""}`} onClick={() => setReiter(id)}>
            {text}
          </button>
        ))}
      </div>
      {reiter === "dateien" ? (
        <DateienReiter leadId={lead.id} />
      ) : (
        <>
          <UeberAbschnitt lead={lead} onLead={onLead} zu={ueberZu} onZu={setUeberZu} />
          <AufgabenAbschnitt leadId={lead.id} aufgaben={tasks} onAufgabe={onAufgabe} />
          <OpportunitiesAbschnitt leadId={lead.id} leadName={lead.display_name ?? lead.name} opportunities={opportunities} kontakte={contacts} />
          <KontakteKarte leadId={lead.id} leadName={lead.display_name ?? lead.name} kontakte={contacts} hervorheben={hervorheben} onKontakt={onKontakt} onOrdnen={onKontakteOrdnen} />
          <FelderAbschnitt lead={lead} kontakte={contacts} onLead={onLead} />
        </>
      )}
    </>
  );
}

/** Website wie Close anzeigen: ohne https://, ohne www., ohne Schrägstrich am Ende. */
export function urlAnzeige(url: string): string {
  return url.trim().replace(/^https?:\/\//i, "").replace(/^www\./i, "").replace(/\/+$/, "");
}

const URL_MUSTER = /((?:https?:\/\/|www\.)[^\s<>"']+)/gi;

/** Text mit anklickbaren Links (nur http, https und www.), Satzzeichen am Ende gehören nicht zum Link. */
export function mitLinks(text: string): ReactNode[] {
  return text.split(URL_MUSTER).map((teil, i) => {
    if (i % 2 === 0) return teil;
    const ende = /[.,;:!?)\]]+$/.exec(teil)?.[0] ?? "";
    const url = ende ? teil.slice(0, -ende.length) : teil;
    const href = /^https?:/i.test(url) ? url : `https://${url}`;
    return (
      <Fragment key={i}>
        <a href={href} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
          {url}
        </a>
        {ende}
      </Fragment>
    );
  });
}

/** Follow-ups wie Close „TASKS“: „+“ steht immer rechts neben der Überschrift (auch zugeklappt oder leer) und legt wie der Follow-up-Knopf
 * im Kopf sofort ein Follow-up „Follow-up“ für morgen an, ohne Formular und ohne Nachfrage (Dilsar, 18.10.2026).
 * Sortiert wie die Inbox: Hoch zuerst, dann nach Fälligkeit. */
function AufgabenAbschnitt({ leadId, aufgaben, onAufgabe }: { leadId: string; aufgaben: Aufgabe[]; onAufgabe: (a: Aufgabe) => void }) {
  const { anlegen, laeuft } = useFollowUpAnlegen(onAufgabe);
  const liste = aufgabenSortieren(aufgaben);
  const schnell = () => void anlegen({ lead_id: leadId, text: "Follow-up", date: tagPlus(heute(), 1) });
  return (
    <Abschnitt
      id="aufgaben"
      icon="aufgabe"
      titel={T.lead.aufgaben}
      zahl={aufgaben.length}
      leer={aufgaben.length === 0}
      immerAuf={aufgaben.length > 0}
      aktionenImmer
      aktionen={
        <button type="button" className="symbolknopf symbolknopf--klein" title="Follow-up für morgen anlegen" aria-label="Follow-up anlegen" data-follow-up-plus="" disabled={laeuft} onClick={schnell}>
          <Icon name="plus" groesse={14} />
        </button>
      }
    >
      {liste.length === 0 ? (
        <p className="ls-leer">
          Keine offenen Follow-ups.{" "}
          <button type="button" className="textknopf" disabled={laeuft} onClick={schnell}>
            Follow-up anlegen
          </button>
        </p>
      ) : (
        <div className="ls-aufgaben">
          {liste.map((a) => (
            <div key={a.id} className="ls-aufgabe">
              <Avatar name={a.assigned_to_name} id={a.assigned_to} groesse={26} titel={a.assigned_to_name ? `Zugewiesen: ${a.assigned_to_name}` : undefined} />
              <AufgabeZeile aufgabe={a} onGeaendert={onAufgabe} />
            </div>
          ))}
        </div>
      )}
    </Abschnitt>
  );
}

function UeberAbschnitt({ lead, onLead, zu, onZu }: { lead: Lead; onLead: (l: Lead) => void; zu: boolean; onZu: (z: boolean) => void }) {
  const meldung = useMeldung();
  const [feld, setFeld] = useState<"url" | "description" | "adresse" | null>(null);
  const [wert, setWert] = useState("");

  const speichern = async (name: "url" | "description") => {
    setFeld(null);
    const neu = wert.trim() || null;
    if (neu === (lead[name] ?? null)) return;
    const vorher = lead;
    onLead({ ...lead, [name]: neu });
    try {
      const r = await leadsApi.aendern(lead.id, { [name]: neu });
      onLead(r.lead);
    } catch (e) {
      onLead(vorher);
      meldung.fehler(e, name === "url" ? "Website nicht gespeichert" : "Beschreibung nicht gespeichert");
    }
  };

  const bearbeiter = (name: "url" | "description") =>
    name === "description" ? (
      <textarea
        className="eingabe eingabe--text"
        rows={3}
        value={wert}
        autoFocus
        onChange={(e) => setWert(e.target.value)}
        onBlur={() => void speichern(name)}
        onKeyDown={(e) => {
          if (e.key === "Escape") setFeld(null);
          if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) (e.target as HTMLTextAreaElement).blur();
        }}
      />
    ) : (
      <input
        className="eingabe"
        value={wert}
        autoFocus
        placeholder="https://"
        onChange={(e) => setWert(e.target.value)}
        onBlur={() => void speichern(name)}
        onKeyDown={(e) => {
          if (e.key === "Escape") setFeld(null);
          if (e.key === "Enter") (e.target as HTMLInputElement).blur();
        }}
      />
    );

  const adressen = lead.addresses ?? [];
  const url = lead.url ? (/^https?:\/\//i.test(lead.url) ? lead.url : `https://${lead.url}`) : null;
  const beschreibungBearbeiten = () => {
    setWert(lead.description ?? "");
    setFeld("description");
  };
  return (
    <Abschnitt id="ueber" icon="info" titel={T.lead.ueber} zuGesteuert={zu} onZuGesteuert={onZu}>
      <ul className="ls-ueber">
        {feld === "adresse" ? (
          <li className="ls-ueber__adressform">
            <Icon name="ort" groesse={13} />
            <AdresseBearbeiten lead={lead} onLead={onLead} onFertig={() => setFeld(null)} />
          </li>
        ) : adressen.length ? (
          adressen.map((a, i) => {
            const strasse = [a.address_1, a.address_2].filter(Boolean).join(", ");
            const ort = [[a.zipcode, a.city].filter(Boolean).join(" "), a.country && a.country !== "DE" ? a.country : null].filter(Boolean).join(", ");
            const text = [strasse, ort].filter(Boolean).join(", ");
            return (
              <li key={i} className="ls-ueber__adresse">
                <Icon name="ort" groesse={13} />
                <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(text)}`} target="_blank" rel="noopener noreferrer" title="In Google Maps öffnen">
                  {strasse ? <span>{strasse}</span> : null}
                  {ort ? <span>{ort}</span> : null}
                  {!strasse && !ort ? <span>Adresse</span> : null}
                </a>
                {i === adressen.length - 1 ? (
                  <button type="button" className="symbolknopf symbolknopf--klein ls-ueber__stift" title={TL.adresse.bearbeiten} aria-label={TL.adresse.bearbeiten} onClick={() => setFeld("adresse")}>
                    <Icon name="stift" groesse={12} />
                  </button>
                ) : null}
              </li>
            );
          })
        ) : (
          <li className="ls-leer">
            <Icon name="ort" groesse={13} />
            <button type="button" className="ls-ueber__hinzu" onClick={() => setFeld("adresse")}>
              {TL.adresse.hinzufuegen}
            </button>
          </li>
        )}
        <li>
          <Icon name="link" groesse={13} />
          {feld === "url" ? (
            bearbeiter("url")
          ) : (
            <span className="ls-ueber__wert">
              {url ? (
                <a href={url} target="_blank" rel="noopener noreferrer" title={url}>
                  {urlAnzeige(lead.url ?? "")}
                </a>
              ) : null}
              <button
                type="button"
                className={url ? "symbolknopf symbolknopf--klein ls-ueber__stift" : "ls-ueber__hinzu"}
                title="Website bearbeiten"
                onClick={() => {
                  setWert(lead.url ?? "");
                  setFeld("url");
                }}
              >
                {url ? <Icon name="stift" groesse={12} /> : "Website hinzufügen …"}
              </button>
            </span>
          )}
        </li>
        <li>
          <Icon name="text" groesse={13} />
          {feld === "description" ? (
            bearbeiter("description")
          ) : (
            <div
              role="button"
              tabIndex={0}
              className="ls-ueber__beschreibung"
              title="Beschreibung bearbeiten"
              onClick={(e) => {
                if (!(e.target as HTMLElement).closest("a")) beschreibungBearbeiten();
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" && e.target === e.currentTarget) beschreibungBearbeiten();
              }}
            >
              {lead.description ? <span className="mehrzeilig">{mitLinks(lead.description)}</span> : <span className="ls-ueber__hinzu">Beschreibung hinzufügen …</span>}
            </div>
          )}
        </li>
      </ul>
      {/* Integration Links (Einstellungen › Links, Agent 8B); die Komponente zeigt ohne Links nichts */}
      <IntegrationLinks objekt="lead" objektId={lead.id} />
    </Abschnitt>
  );
}
